/**
 * ContractViolationBanner — every major contract violation the reporter raised, until
 * dismissed. It raises one only where LOUD_SCHEMA_ERRORS is on, and the bar says so.
 */

import { TriangleAlert } from 'lucide-react';
import type { ReactElement } from 'react';

import { Hint } from '@/components/occurra/Hint';
import { CancelIcon } from '@/components/occurra/icons/CancelIcon';
import { useContractViolations } from '@/hooks/useContractViolations';
import { cn } from '@/lib/utils';
import { getViolationText, LOUD_SCHEMA_ERRORS_NOTE } from '@/lib/violationText';

export const ContractViolationBanner = (): ReactElement | null => {
    const { violations, dismiss } = useContractViolations();
    const latestViolation = violations.at(-1);
    if (latestViolation === undefined) {
        return null;
    }
    const summary = `Contract violations: ${violations.length} — latest from ${
        latestViolation.endpoint}`;
    return (
        <div
            role="alert"
            className="shrink-0 bg-occ-background-panel shadow-md"
        >
            <div className={cn(
                'flex items-start gap-3 px-4 py-2.5',
                'bg-occ-red-200 text-sm text-occ-foreground-high-contrast',
                'pt-[max(0.625rem,env(safe-area-inset-top))]',
            )}
            >
                <TriangleAlert className="size-5 shrink-0" />
                <div className="min-w-0 flex-1">
                    <details>
                        <summary className={cn(
                            '-mx-1 rounded px-1 font-medium',
                            'hover:bg-occ-foreground-25 active:bg-occ-foreground-50',
                        )}
                        >
                            {summary}
                        </summary>
                        <ul className="mt-2 max-h-48 space-y-1 overflow-auto font-mono text-xs">
                            {violations.map((violation, index) => (
                                <li key={index}>{getViolationText(violation)}</li>
                            ))}
                        </ul>
                    </details>
                    <p className="text-xs text-occ-foreground-secondary">
                        {LOUD_SCHEMA_ERRORS_NOTE}
                    </p>
                </div>
                <Hint label="Dismiss">
                    <button
                        type="button"
                        onClick={dismiss}
                        aria-label="Dismiss contract violations"
                        className={cn(
                            'shrink-0 rounded p-1',
                            'not-disabled:hover:bg-occ-foreground-25 active:bg-occ-foreground-50',
                        )}
                    >
                        <CancelIcon className="size-4" />
                    </button>
                </Hint>
            </div>
        </div>
    );
};
